import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import moment from 'moment'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import Slide from '@mui/material/Slide'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'

import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded'
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded'
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined'

import Langs, { Localize } from '../../app/lang/langs'
import { showErrorToast } from '../../app/utils/helpers'
import {
    clearAttempt,
    getVLearnQuizForLearner,
    listVLearnMyAttempts,
    patchLessonProgressLocal,
    startVLearnAttempt,
    submitVLearnAttempt
} from './vlearnSlice'
import QuizAttempt from '../../components/vlearn/quiz/QuizAttempt'
import QuizResult from '../../components/vlearn/quiz/QuizResult'
import QuizReview from '../../components/vlearn/quiz/QuizReview'
import { vlearnGrid, vlearnSurface, vlearnType, VLearnEmptyState, VLearnStatTile } from '../../components/vlearn/vlearnUi'

const SlideUp = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />)

/**
 * V-Learning — the learner's quiz screen.
 *
 * Owns the four phases of sitting an assessment (the pre-attempt briefing, the paper, the
 * result, the correction) and is the only place in the quiz UI that talks to Redux. The
 * four components below it are presentational: they are handed what the server said and
 * they render it, which is what makes them testable and what keeps the assessment policy in
 * exactly one place.
 *
 * ── What this deliberately does differently from V-Sécurité ──────────────────────────
 *
 * `QuizTake.js` starts an attempt in a mount effect, so a reload is a new attempt and
 * `max_attempts` cannot be enforced. Here nothing is started until the learner presses the
 * primary button, and `startVLearnAttempt` RESUMES an unfinished attempt rather than
 * creating a second one — so the button says "Resume", not "Start", whenever the server
 * reports an `inProgressAttempt`. Telling the learner they are resuming matters: a paper
 * with six minutes left on its clock is not the same offer as a fresh twenty.
 *
 * `startedRef` guards the dispatch itself. React 18's StrictMode double-invokes effects and
 * a fast double-click is the same hazard, and starting an attempt is a write — the same
 * reason `QuizTake.js:91` carries one.
 *
 * @param {object} props
 * @param {number} props.idCourse the course the quiz belongs to; every endpoint re-checks it
 * @param {number} props.idQuiz
 * @param {number} [props.idLesson] the lesson carrying this quiz, if any
 * @param {Function} props.onExit called when the learner leaves the quiz for good
 */
export default function QuizPlayer({ idCourse, idQuiz, idLesson, onExit }) {
    const dispatch = useDispatch()
    const lang = useSelector((state) => state.settings.lang)
    const isMobileRedux = useSelector((state) => state.settings.isMobile)

    const theme = useTheme()
    const isSmallQuery = useMediaQuery(theme.breakpoints.down('sm'))
    const isSmall = isMobileRedux === 2 || isSmallQuery

    const access = useSelector((state) => state.vlearn.access)
    const forLearner = useSelector((state) => state.vlearn.quizForLearner)
    const storedAttempt = useSelector((state) => state.vlearn.attempt)
    const attemptResult = useSelector((state) => state.vlearn.attemptResult)

    /** 'intro' | 'attempt' | 'result' | 'review' */
    const [phase, setPhase] = useState('intro')
    const [reviewAttemptId, setReviewAttemptId] = useState(null)

    /**
     * True from the moment a start dispatch leaves until the attempt is closed.
     *
     * Not derived from `storedAttempt`, because the window that matters is the one while
     * the request is in flight and the store still holds nothing.
     */
    const startedRef = useRef(false)

    const quiz = forLearner && forLearner.quiz ? forLearner.quiz : null
    const attempts = useMemo(
        () => (forLearner && Array.isArray(forLearner.attempts) ? forLearner.attempts : []),
        [forLearner])

    const refresh = useCallback(() => {
        dispatch(getVLearnQuizForLearner({ id_course: idCourse, id_quiz: idQuiz }))
        // The switcher inside QuizReview reads `state.vlearn.myAttempts`, so it is filled
        // here rather than from inside that component: on an `after_pass` policy the
        // attempt the learner opened may be the one they are not allowed to see, and the
        // list is what lets them switch to the one they are.
        dispatch(listVLearnMyAttempts({ id_course: idCourse, id_quiz: idQuiz }))
    }, [dispatch, idCourse, idQuiz])

    useEffect(() => {
        if (idCourse && idQuiz) refresh()
    }, [idCourse, idQuiz, refresh])

    // Leaving the screen must not leave a finished paper in the store: the next quiz would
    // render under the previous one's result for as long as its own request takes.
    useEffect(() => () => { dispatch(clearAttempt()) }, [dispatch])

    // ── The primary action ───────────────────────────────────────────────────────────

    const inProgress = forLearner ? forLearner.inProgressAttempt : null
    /** Null means unlimited — the server sends null rather than a number on purpose. */
    const attemptsLeft = forLearner ? forLearner.attemptsLeft : null
    const questionsCount = quiz ? Number(quiz.questions_count) || 0 : 0

    /**
     * Whether a previous attempt already passed.
     *
     * `passed` is only in the payload when `show_score` is on, so this is `false` — never
     * "unknown treated as passed" — for a quiz whose results are withheld. A learner in
     * that case keeps their remaining attempts, which is the safe way to be wrong.
     */
    const alreadyPassed = attempts.some((attempt) => attempt.passed === true)

    const blockedReason = useMemo(() => {
        if (!quiz) return null
        if (!questionsCount) return 'VLEARN_QUIZ_BLOCKED_NO_QUESTIONS'
        // An author previewing their own unpublished course is not a learner on it, so
        // `startAttempt` requires canLearn and answers 403. The button used to be fully
        // enabled and the refusal arrived as a blunt untranslated toast; saying why up front
        // is better than offering an action that cannot succeed. Checked after the
        // no-questions case, which is the more actionable message for an author.
        if (access && access.canLearn === false) return 'VLEARN_QUIZ_BLOCKED_NOT_A_LEARNER'
        // An in-progress attempt is always resumable: it was already paid for out of the
        // allowance, and refusing to let the learner finish it would be the worst outcome.
        if (inProgress) return null
        if (attemptsLeft !== null && Number(attemptsLeft) <= 0) return 'VLEARN_QUIZ_BLOCKED_NO_ATTEMPTS_LEFT'
        if (alreadyPassed) return 'VLEARN_QUIZ_BLOCKED_ALREADY_PASSED'
        return null
    }, [quiz, questionsCount, inProgress, attemptsLeft, alreadyPassed, access])

    const primaryKey = inProgress
        ? 'VLEARN_QUIZ_BTN_RESUME'
        : attempts.length ? 'VLEARN_QUIZ_BTN_RETAKE' : 'VLEARN_QUIZ_BTN_START'

    const handleStart = useCallback(async () => {
        if (startedRef.current) return
        startedRef.current = true

        const action = await dispatch(startVLearnAttempt({ id_course: idCourse, id_quiz: idQuiz }))
        const data = action && action.payload && action.payload.responseData

        if (!data || data.status !== 1 || !data.attempt || !Array.isArray(data.questions) || !data.questions.length) {
            startedRef.current = false
            const message = action && action.payload && action.payload.responseError
                && action.payload.responseError.data && action.payload.responseError.data.error
            showErrorToast(message || Localize('VLEARN_QUIZ_TOAST_START_FAILED', lang))
            // The allowance may be the reason, so the briefing is re-read rather than left
            // showing a button the server has just refused.
            refresh()
            return
        }

        setPhase('attempt')
    }, [dispatch, idCourse, idQuiz, lang, refresh])

    const handleSubmit = useCallback(async ({ answers, secondsSpent }) => {
        // `false` so QuizAttempt releases its lock — see the note there. A bare return left
        // the paper stuck on "Submitting…" with the answers unreachable.
        if (!storedAttempt || !storedAttempt.attempt) return false

        // One request carrying the whole paper. V-Sécurité posts one per question and then a
        // finish call — 21 round trips for a 20-question quiz, with nothing to roll back when
        // the eleventh fails and the learner is left half-graded.
        const action = await dispatch(submitVLearnAttempt({
            id_course: idCourse,
            id_attempt: storedAttempt.attempt.id,
            answers,
            seconds_spent: secondsSpent
        }))
        const data = action && action.payload && action.payload.responseData

        if (!data || data.status !== 1) {
            const message = action && action.payload && action.payload.responseError
                && action.payload.responseError.data && action.payload.responseError.data.error
            showErrorToast(message || Localize('VLEARN_QUIZ_TOAST_SUBMIT_FAILED', lang))
            // `false`, not a bare return: QuizAttempt releases its submit lock on a falsy
            // result, so the learner can retry with the answers still on screen.
            return false
        }

        // The attempt is closed, so a retake is allowed to start a new one.
        startedRef.current = false

        // The backend marks the carrying lesson complete when the learner passes. Patching
        // the curriculum locally keeps the player's sidebar honest without refetching the
        // whole course and losing its scroll position. Only an explicit `true` triggers it:
        // with `show_score` off the field is absent, and an absent verdict is not a pass.
        if (idLesson && data.attempt && data.attempt.passed === true) {
            // `my_status`, not `status`: that is the field the curriculum rows carry, and
            // what CurriculumSidebar and CourseCard read. Writing `status` added a key
            // nothing reads, so the completion tick never appeared after passing a quiz.
            dispatch(patchLessonProgressLocal({
                idLesson,
                changes: { my_status: 'completed', completed_at: new Date().toISOString() }
            }))
        }

        setPhase('result')
        // Refresh the attempt history before returning: the result screen offers "Review"
        // and a retake, and both read from it.
        refresh()
        // Truthy so QuizAttempt keeps its submit lock held — see the note there.
        return true
    }, [dispatch, idCourse, idLesson, lang, refresh, storedAttempt])

    /**
     * Leaves the paper without submitting.
     *
     * The attempt stays `in_progress` server-side and its clock keeps running, so the
     * briefing will offer "Resume". Nothing is discarded and nothing new is created.
     */
    const handleAbandon = useCallback(() => {
        startedRef.current = false
        dispatch(clearAttempt())
        setPhase('intro')
        refresh()
    }, [dispatch, refresh])

    const handleRetake = useCallback(() => {
        dispatch(clearAttempt())
        setPhase('intro')
    }, [dispatch])

    const openReview = useCallback((idAttempt) => {
        setReviewAttemptId(idAttempt)
        setPhase('review')
    }, [])

    const closeReview = useCallback(() => {
        setReviewAttemptId(null)
        // Coming back from a correction lands on the result when there is one to land on,
        // so a learner who reviewed straight after submitting does not lose their score.
        setPhase(attemptResult ? 'result' : 'intro')
    }, [attemptResult])

    // ── Phases ──────────────────────────────────────────────────────────────────────

    if (phase === 'review' && reviewAttemptId) {
        return <QuizReview idCourse={idCourse} idAttempt={reviewAttemptId} onBack={closeReview} />
    }

    // `attemptResult.attempt` as well as `attemptResult`: QuizResult returns null without
    // it, which would blank the screen at the worst possible moment — immediately after the
    // learner pressed Submit. Falling through to the briefing at least leaves them an exit.
    if (phase === 'result' && attemptResult && attemptResult.attempt) {
        return (
            <QuizResult
                result={attemptResult}
                quiz={quiz}
                onRetake={handleRetake}
                onReview={() => openReview(attemptResult.attempt && attemptResult.attempt.id)}
                onExit={onExit}
            />
        )
    }

    if (phase === 'attempt' && storedAttempt && storedAttempt.attempt) {
        /**
         * The paper rides on the `attempt` prop rather than on `quiz`, so `QuizAttempt` can
         * read `attempt.expires_at` — the server's own deadline — without reaching through
         * two levels of the response.
         */
        const paper = {
            ...storedAttempt.attempt,
            resumed: !!storedAttempt.resumed,
            questions: storedAttempt.questions || []
        }
        const attemptScreen = (
            <QuizAttempt
                attempt={paper}
                quiz={storedAttempt.quiz || quiz}
                onSubmit={handleSubmit}
                onAbandon={handleAbandon}
            />
        )

        // Full-screen on a phone, inline on a desktop — the same arrangement
        // `VSecuriteView` uses for `QuizTake`, because a sticky header and a sticky submit
        // bar inside a short scroll container is unusable on a small screen.
        return isSmall ? (
            <Dialog
                open
                fullScreen
                slots={{ transition: SlideUp }}
                sx={{ zIndex: 1400 }}
                slotProps={{ paper: { sx: { bgcolor: 'transparent', boxShadow: 'none' } } }}
            >
                {attemptScreen}
            </Dialog>
        ) : attemptScreen
    }

    // ── The briefing ────────────────────────────────────────────────────────────────

    if (!quiz) {
        return (
            <Box sx={{ ...vlearnSurface.card }}>
                <VLearnEmptyState
                    icon={<QuizOutlinedIcon />}
                    title={<Langs str="VLEARN_QUIZ_LOADING_TITLE" />}
                    description={<Langs str="VLEARN_QUIZ_LOADING_BODY" />}
                    // `onExit` is in scope and used further down, but this branch offered no
                    // action — so a deleted quiz, a 403 or a dropped connection left the
                    // learner on a permanent "Loading the quiz…" panel with no way out.
                    action={onExit ? (
                        <Button variant="outlined" size="small" onClick={onExit}
                            sx={{ textTransform: 'none' }}>
                            <Langs str="VLEARN_BACK" />
                        </Button>
                    ) : null}
                />
            </Box>
        )
    }

    const showsScores = attempts.some((attempt) => typeof attempt.percent === 'number')

    return (
        <Box component="section" aria-labelledby="vlearn-quiz-title" sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, paddingTop: '20px' }}>

            <Box sx={{ ...vlearnSurface.card, padding: { xs: '20px', sm: '28px 32px' } }}>
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                    <Box sx={{
                        width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
                        backgroundColor: 'rgba(142, 78, 198, 0.12)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <QuizOutlinedIcon sx={{ color: '#8E4EC6', fontSize: 24 }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography id="vlearn-quiz-title" component="h2" sx={{ ...vlearnType.pageTitle }}>
                            {quiz.title}
                        </Typography>
                        {quiz.description ? (
                            <Typography sx={{ ...vlearnType.body, mt: 0.75, maxWidth: 640 }}>
                                {quiz.description}
                            </Typography>
                        ) : null}
                        {quiz.is_final ? (
                            <Chip
                                size="small"
                                label={<Langs str="VLEARN_QUIZ_FINAL_BADGE" />}
                                sx={{
                                    mt: 1.25, fontWeight: 600, fontSize: '0.72rem', borderRadius: '7px',
                                    backgroundColor: 'rgba(142, 78, 198, 0.12)', color: '#6D28A8'
                                }}
                            />
                        ) : null}
                    </Box>
                </Stack>

                <Box sx={{ ...vlearnGrid.tiles, mt: 3 }}>
                    <VLearnStatTile
                        label={<Langs str="VLEARN_QUIZ_STAT_QUESTIONS" />}
                        value={questionsCount}
                        hint={Number(quiz.total_points) > 0
                            ? Localize(Number(quiz.total_points) === 1
                                ? 'VLEARN_POINTS_ONE' : 'VLEARN_POINTS_MANY', lang)
                                .replace('%points%', formatPoints(quiz.total_points))
                            : null}
                    />
                    <VLearnStatTile
                        label={<Langs str="VLEARN_QUIZ_STAT_PASS_MARK" />}
                        value={`${Number(quiz.passing_percent)}%`}
                    />
                    <VLearnStatTile
                        label={<Langs str="VLEARN_QUIZ_STAT_ATTEMPTS" />}
                        value={attemptsLeft === null
                            ? Localize('VLEARN_QUIZ_ATTEMPTS_UNLIMITED', lang)
                            : Localize('VLEARN_QUIZ_ATTEMPTS_NTH_OF_MAX', lang)
                                .replace('%n%', Math.min(attempts.length + (inProgress ? 0 : 1), Number(quiz.max_attempts)))
                                .replace('%max%', Number(quiz.max_attempts))}
                        hint={attemptsLeft === null ? null
                            : Number(attemptsLeft) === 1
                                ? Localize('VLEARN_QUIZ_ATTEMPTS_LEFT_ONE', lang)
                                : Localize('VLEARN_QUIZ_ATTEMPTS_LEFT_MANY', lang).replace('%n%', Number(attemptsLeft))}
                    />
                    <VLearnStatTile
                        label={<Langs str="VLEARN_QUIZ_STAT_TIME_LIMIT" />}
                        value={quiz.time_limit_minutes
                            ? Localize('VLEARN_QUIZ_MINUTES', lang).replace('%n%', Number(quiz.time_limit_minutes))
                            : Localize('VLEARN_QUIZ_NO_TIME_LIMIT', lang)}
                        icon={<ScheduleRoundedIcon fontSize="small" />}
                    />
                </Box>

                {inProgress ? (
                    <Box sx={{
                        mt: 2.5, padding: '14px 16px', borderRadius: '12px',
                        backgroundColor: 'rgba(255, 144, 21, 0.09)',
                        border: '1px solid rgba(255, 144, 21, 0.28)'
                    }}>
                        <Typography sx={{ ...vlearnType.body, color: '#B25E00', fontWeight: 600 }}>
                            <Langs
                                str="VLEARN_QUIZ_RESUME_NOTICE"
                                params={{ n: Number(inProgress.attempt_no) }}
                            />
                        </Typography>
                        {inProgress.expires_at ? (
                            <Typography sx={{ ...vlearnType.meta, mt: 0.5 }}>
                                <Langs
                                    str="VLEARN_QUIZ_RESUME_DEADLINE"
                                    params={{ time: moment(inProgress.expires_at).format('HH:mm') }}
                                />
                            </Typography>
                        ) : null}
                    </Box>
                ) : null}

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    sx={{ mt: 3 }}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                    {/* A disabled MUI button swallows pointer events, so the Tooltip needs a
                        wrapper element of its own to hang the explanation on. Without it the
                        learner sees a dead button and no reason. */}
                    <Tooltip
                        title={blockedReason ? Localize(blockedReason, lang) : ''}
                        disableHoverListener={!blockedReason}
                        disableFocusListener={!blockedReason}
                    >
                        <Box component="span" sx={{ display: 'inline-flex' }}>
                            <Button
                                variant="contained"
                                disableElevation
                                disabled={!!blockedReason}
                                onClick={handleStart}
                                startIcon={inProgress ? <PlayArrowRoundedIcon /> : attempts.length ? <ReplayRoundedIcon /> : <PlayArrowRoundedIcon />}
                                sx={primaryButtonSx}
                            >
                                <Langs str={primaryKey} />
                            </Button>
                        </Box>
                    </Tooltip>

                    {onExit ? (
                        <Button
                            variant="text"
                            onClick={onExit}
                            startIcon={<ArrowBackRoundedIcon />}
                            sx={quietButtonSx}
                        >
                            <Langs str="VLEARN_QUIZ_BTN_LEAVE" />
                        </Button>
                    ) : null}
                </Stack>
            </Box>

            {/* ── Previous attempts ───────────────────────────────────────────────── */}
            <Box sx={{ ...vlearnSurface.card }}>
                <Box sx={{ padding: '18px 20px 12px' }}>
                    <Typography component="h3" sx={{ ...vlearnType.sectionTitle }}>
                        <Langs str="VLEARN_QUIZ_HISTORY_TITLE" />
                    </Typography>
                </Box>

                {attempts.length && isSmall ? (
                    /*
                     * A STACKED LIST ON A PHONE, not the table.
                     *
                     * The table below is `minWidth: 560` inside an `overflowX: auto` wrapper,
                     * which is the right call on a desktop and honest about not widening the
                     * page. But at 360px it means the score — the one column a learner opens
                     * this panel to see — is off-screen to the right, behind a sideways drag
                     * whose column headers scroll away as you drag. Six fields per attempt is
                     * a short list, not a table, once the width is gone.
                     */
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, px: 2, pb: 2 }}>
                        {[...attempts].reverse().map((attempt) => (
                            <Box
                                key={attempt.id}
                                sx={{
                                    border: '1px solid rgba(0,0,0,0.08)', borderRadius: '12px',
                                    padding: '12px 14px'
                                }}
                            >
                                <Box sx={{
                                    display: 'flex', alignItems: 'center',
                                    justifyContent: 'space-between', gap: 1
                                }}>
                                    <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.88rem' }}>
                                        {`#${attempt.attempt_no}`}
                                    </Typography>
                                    <Chip
                                        size="small"
                                        label={<Langs str={attemptStatusKey(attempt)} />}
                                        sx={{
                                            ...attemptStatusStyle(attempt),
                                            fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: '7px'
                                        }}
                                    />
                                </Box>

                                <Typography sx={{ ...vlearnType.meta, mt: 0.5 }}>
                                    {attempt.submitted_at || attempt.started_at
                                        ? moment(attempt.submitted_at || attempt.started_at).format('DD/MM/YYYY HH:mm')
                                        : '—'}
                                </Typography>

                                {/* Only when the payload actually carries scores:
                                    `show_score = 0` omits the fields server-side, and an
                                    empty row would be a lie about the data rather than a gap
                                    in it. Same rule as the table's score columns. */}
                                {typeof attempt.percent === 'number' ? (
                                    <Typography sx={{
                                        mt: 0.75, fontSize: '0.95rem', fontWeight: 700,
                                        fontVariantNumeric: 'tabular-nums',
                                        color: 'var(--text-primary, #111318)'
                                    }}>
                                        {`${Math.round(attempt.percent)}%`}
                                        {typeof attempt.score_points === 'number'
                                            && typeof attempt.max_points === 'number' ? (
                                            <Typography component="span" sx={{ ...vlearnType.meta, ml: 1 }}>
                                                {`${formatPoints(attempt.score_points)} / ${formatPoints(attempt.max_points)}`}
                                            </Typography>
                                        ) : null}
                                    </Typography>
                                ) : null}

                                {attempt.canReview ? (
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        fullWidth
                                        startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                                        onClick={() => openReview(attempt.id)}
                                        sx={{ ...quietButtonSx, mt: 1, minHeight: 44 }}
                                    >
                                        <Langs str="VLEARN_QUIZ_BTN_REVIEW" />
                                    </Button>
                                ) : null}
                            </Box>
                        ))}
                    </Box>
                ) : attempts.length ? (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Table size="small" sx={{ minWidth: showsScores ? 560 : 420 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={headCellSx}><Langs str="VLEARN_QUIZ_COL_ATTEMPT" /></TableCell>
                                    <TableCell sx={headCellSx}><Langs str="VLEARN_QUIZ_COL_DATE" /></TableCell>
                                    <TableCell sx={headCellSx}><Langs str="VLEARN_QUIZ_COL_STATUS" /></TableCell>
                                    {/* The score columns exist only when the payload actually
                                        carries scores. `show_score = 0` omits the fields
                                        server-side, so an empty column would be a lie about
                                        the data rather than a gap in it. */}
                                    {showsScores ? (
                                        <>
                                            <TableCell sx={headCellSx} align="right"><Langs str="VLEARN_QUIZ_COL_SCORE" /></TableCell>
                                            <TableCell sx={headCellSx} align="right"><Langs str="VLEARN_QUIZ_COL_POINTS" /></TableCell>
                                        </>
                                    ) : null}
                                    <TableCell sx={headCellSx} align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {[...attempts].reverse().map((attempt) => (
                                    <TableRow key={attempt.id} hover>
                                        <TableCell sx={bodyCellSx}>
                                            <Typography component="span" sx={{ fontWeight: 700, fontSize: '0.82rem' }}>
                                                {`#${attempt.attempt_no}`}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            {attempt.submitted_at || attempt.started_at
                                                ? moment(attempt.submitted_at || attempt.started_at).format('DD/MM/YYYY HH:mm')
                                                : '—'}
                                        </TableCell>
                                        <TableCell sx={bodyCellSx}>
                                            <Chip
                                                size="small"
                                                label={<Langs str={attemptStatusKey(attempt)} />}
                                                sx={{
                                                    ...attemptStatusStyle(attempt),
                                                    fontWeight: 600, fontSize: '0.7rem', height: 22, borderRadius: '7px'
                                                }}
                                            />
                                        </TableCell>
                                        {showsScores ? (
                                            <>
                                                <TableCell sx={bodyCellSx} align="right">
                                                    {typeof attempt.percent === 'number'
                                                        ? `${Math.round(attempt.percent)}%` : '—'}
                                                </TableCell>
                                                <TableCell sx={bodyCellSx} align="right">
                                                    {typeof attempt.score_points === 'number' && typeof attempt.max_points === 'number'
                                                        ? `${formatPoints(attempt.score_points)} / ${formatPoints(attempt.max_points)}`
                                                        : '—'}
                                                </TableCell>
                                            </>
                                        ) : null}
                                        <TableCell sx={bodyCellSx} align="right">
                                            {attempt.canReview ? (
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    startIcon={<VisibilityOutlinedIcon fontSize="small" />}
                                                    onClick={() => openReview(attempt.id)}
                                                    sx={{ ...quietButtonSx, minHeight: 34 }}
                                                >
                                                    <Langs str="VLEARN_QUIZ_BTN_REVIEW" />
                                                </Button>
                                            ) : null}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                ) : (
                    <VLearnEmptyState
                        icon={<QuizOutlinedIcon />}
                        title={<Langs str="VLEARN_QUIZ_HISTORY_EMPTY_TITLE" />}
                        description={<Langs str="VLEARN_QUIZ_HISTORY_EMPTY_BODY" />}
                    />
                )}
            </Box>
        </Box>
    )
}

// ── Local helpers ───────────────────────────────────────────────────────────────────

/** Points are `decimal(8,2)`; trailing zeros on a whole number are noise on a score line. */
function formatPoints(value) {
    const number = Number(value)
    if (!Number.isFinite(number)) return '0'
    return Number.isInteger(number) ? String(number) : number.toFixed(2).replace(/0$/, '')
}

/**
 * The status label for one attempt row.
 *
 * `passed` is consulted before `status` because "submitted" is not what the learner wants
 * to know, and it is skipped entirely when the field is absent — a withheld score must not
 * become a visible verdict by another route.
 */
function attemptStatusKey(attempt) {
    if (attempt.status === 'in_progress') return 'VLEARN_ATTEMPT_STATUS_IN_PROGRESS'
    if (attempt.status === 'expired') return 'VLEARN_ATTEMPT_STATUS_EXPIRED'
    if (attempt.passed === true) return 'VLEARN_ATTEMPT_STATUS_PASSED'
    if (attempt.passed === false) return 'VLEARN_ATTEMPT_STATUS_FAILED'
    return 'VLEARN_ATTEMPT_STATUS_SUBMITTED'
}

function attemptStatusStyle(attempt) {
    if (attempt.status === 'in_progress') {
        return { backgroundColor: 'rgba(255, 144, 21, 0.14)', color: '#B25E00' }
    }
    if (attempt.status === 'expired') {
        return { backgroundColor: 'rgba(251, 105, 98, 0.16)', color: '#B3271F' }
    }
    if (attempt.passed === true) {
        return { backgroundColor: 'rgba(58, 156, 96, 0.14)', color: '#1F7A4C' }
    }
    if (attempt.passed === false) {
        return { backgroundColor: 'rgba(251, 105, 98, 0.16)', color: '#B3271F' }
    }
    return { backgroundColor: 'var(--lightGrey, #ededed)', color: '#3a3a3a' }
}

const primaryButtonSx = {
    borderRadius: '12px',
    px: 3,
    minHeight: 46,
    textTransform: 'none',
    fontWeight: 650,
    fontSize: '0.92rem',
    backgroundColor: 'var(--orange, rgb(255,144,21))',
    color: '#fff',
    '&:hover': { backgroundColor: '#e07d0d' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' },
    '&.Mui-disabled': { backgroundColor: 'rgba(0,0,0,0.08)', color: 'rgba(0,0,0,0.34)' }
}

const quietButtonSx = {
    borderRadius: '12px',
    minHeight: 46,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.88rem',
    color: 'var(--text-secondary, #4b5263)',
    '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' },
    '&:focus-visible': { outline: '3px solid rgba(255,144,21,0.45)', outlineOffset: '2px' }
}

const headCellSx = {
    fontSize: '0.72rem',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '.04em',
    color: 'var(--text-muted, #8b92a0)',
    borderBottom: '1px solid rgba(0,0,0,0.08)'
}

const bodyCellSx = {
    fontSize: '0.82rem',
    color: 'var(--text-secondary, #4b5263)',
    borderBottom: '1px solid rgba(0,0,0,0.05)'
}
